import React from 'react';
import { Outlet } from 'react-router-dom';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTheme } from '@incubs/ui';
import type { Theme } from '@incubs/ui';
import Navbar from './Navbar';
import Sidebar from './Sidebar';
import { useSchoolTheme, BG_INTENSITY_MULTIPLIER } from '../../hooks/useSchoolTheme';

// ─── Keyframes ────────────────────────────────────────────────────────────────

const auroraShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const rayeScroll = keyframes`
  0%   { background-position: 0 0; }
  100% { background-position: 56px 56px; }
`;

// ─── Shell ────────────────────────────────────────────────────────────────────

const Shell = styled.div<{ $dark: boolean }>`
  display: flex;
  min-height: 100vh;
  /* Warm near-black for dark mode — gives the luxury fashion feel */
  background: ${p => p.$dark ? '#050403' : (p.theme as Theme).colors.bg.primary};
  position: relative;
`;

const AmbientBg = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  overflow: hidden;
`;

// ─── Luxury background layers ─────────────────────────────────────────────────

/* Velours — rich velvet depth, two large diffused colour pools */
const VeloursA = styled.div<{ $c: string; $op: number }>`
  position: absolute;
  top: -30%; left: -20%;
  width: 110%; height: 90%;
  background: radial-gradient(ellipse 65% 55% at 35% 45%, ${p => p.$c} 0%, transparent 65%);
  filter: blur(55px);
  opacity: ${p => p.$op};
`;
const VeloursB = styled.div<{ $c: string; $op: number }>`
  position: absolute;
  bottom: -20%; right: -15%;
  width: 90%; height: 80%;
  background: radial-gradient(ellipse 65% 55% at 65% 55%, ${p => p.$c} 0%, transparent 65%);
  filter: blur(55px);
  opacity: ${p => p.$op};
`;

/* Dégradé — sweeping diagonal silk gradient */
const DegradeLayer = styled.div<{ $p: string; $s: string; $op: number }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    142deg,
    ${p => p.$p} 0%,
    #070402 38%,
    #070402 62%,
    ${p => p.$s} 100%
  );
  opacity: ${p => p.$op};
`;

/* Aurore — slow metallic colour-shift, gold accent woven in */
const AuroreLayer = styled.div<{ $p: string; $s: string; $op: number }>`
  position: absolute;
  inset: 0;
  background: linear-gradient(
    -45deg,
    ${p => p.$p},
    rgba(201, 168, 76, 0.45),
    ${p => p.$s},
    rgba(8, 5, 3, 1),
    ${p => p.$p}
  );
  background-size: 400% 400%;
  animation: ${auroraShift} 20s ease infinite;
  opacity: ${p => p.$op};
`;

/* Damier — checkered luxury grid (Dior / LV inspired) */
const DamierLayer = styled.div<{ $p: string; $op: number }>`
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(45deg,  ${p => p.$p} 25%, transparent 25%),
    linear-gradient(-45deg, ${p => p.$p} 25%, transparent 25%),
    linear-gradient(45deg,  transparent 75%, ${p => p.$p} 75%),
    linear-gradient(-45deg, transparent 75%, ${p => p.$p} 75%);
  background-size: 30px 30px;
  background-position: 0 0, 0 15px, 15px -15px, -15px 0px;
  opacity: ${p => p.$op};
`;

/* Quilté — diagonal diamond grid (Chanel quilted leather) */
const QuilteLayer = styled.div<{ $p: string; $op: number }>`
  position: absolute;
  inset: 0;
  background-image:
    repeating-linear-gradient( 45deg, transparent, transparent 13px, ${p => p.$p} 13px, ${p => p.$p} 14px),
    repeating-linear-gradient(-45deg, transparent, transparent 13px, ${p => p.$p} 14px, ${p => p.$p} 15px);
  opacity: ${p => p.$op};
`;

/* Rayé — fine diagonal pinstripe (Savile Row suiting) */
const RayeLayer = styled.div<{ $p: string; $s: string; $op: number }>`
  position: absolute;
  inset: 0;
  background: repeating-linear-gradient(
    -62deg,
    transparent        0px,  transparent    7px,
    ${p => p.$p}       7px,  ${p => p.$p}   8px,
    transparent        8px,  transparent   18px,
    ${p => p.$s}       18px, ${p => p.$s}  19px,
    transparent       19px,  transparent   28px
  );
  animation: ${rayeScroll} 28s linear infinite;
  opacity: ${p => p.$op};
`;

// ─── Layout ───────────────────────────────────────────────────────────────────

const SidebarWrapper = styled.aside`
  width: 72px;
  flex-shrink: 0;
  @media (max-width: 768px) { display: none; }
`;

const Main = styled.main`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: ${({ theme }) => (theme as Theme).zIndex.sticky};
`;

const Content = styled.div`
  flex: 1;
  padding: 1.75rem 2rem;
  max-width: 1280px;
  width: 100%;
  margin: 0 auto;
  @media (max-width: 640px) { padding: 1.25rem 1rem; }
`;

// ─── Component ────────────────────────────────────────────────────────────────

export default function AppShell() {
  const { mode }                       = useTheme();
  const { theme, bgStyle, bgIntensity } = useSchoolTheme();
  const $dark = mode === 'dark';
  const mul   = BG_INTENSITY_MULTIPLIER[bgIntensity];
  // Cap final opacity at 1.0
  const op = (base: number) => Math.min(base * mul, 1);

  return (
    <Shell $dark={$dark}>
      {bgStyle !== 'minimal' && (
        <AmbientBg>
          {bgStyle === 'orbs' && (
            <>
              <VeloursA $c={theme.primary}   $op={op(0.62)} />
              <VeloursB $c={theme.secondary} $op={op(0.48)} />
            </>
          )}
          {bgStyle === 'gradient' && (
            <DegradeLayer $p={theme.primary} $s={theme.secondary} $op={op(0.72)} />
          )}
          {bgStyle === 'aurora' && (
            <AuroreLayer $p={theme.primary} $s={theme.secondary} $op={op(0.68)} />
          )}
          {bgStyle === 'mesh' && (
            <DamierLayer $p={theme.primary} $op={op(0.18)} />
          )}
          {bgStyle === 'dots' && (
            <QuilteLayer $p={theme.primary} $op={op(0.22)} />
          )}
          {bgStyle === 'waves' && (
            <RayeLayer $p={theme.primary} $s={theme.secondary} $op={op(0.28)} />
          )}
        </AmbientBg>
      )}

      <SidebarWrapper>
        <Sidebar />
      </SidebarWrapper>

      <Main>
        <TopBar>
          <Navbar />
        </TopBar>
        <Content>
          <Outlet />
        </Content>
      </Main>
    </Shell>
  );
}
